/**
 * The "Download" control on the Documents editor (BLA-331).
 *
 * Replaces the single "Download .md" button with a compact disclosure menu
 * offering both export formats — mirrors the collapsed-by-default disclosure
 * pattern already used by {@link VersionsDropdown} rather than pulling in a
 * new menu-component dependency for two items.
 */
import { useState } from "react";

import type { DocumentExportFormat } from "../../utils/download";

/** Props for {@link DownloadMenu}. */
export interface DownloadMenuProps {
  /** Called with the chosen export format when a menu item is picked. */
  onDownload: (format: DocumentExportFormat) => void;
}

/** One selectable format in the menu. */
const FORMATS: {
  format: DocumentExportFormat;
  label: string;
  testId: string;
}[] = [
  { format: "md", label: "Markdown (.md)", testId: "download-md" },
  { format: "pdf", label: "PDF (.pdf)", testId: "download-pdf" },
];

/**
 * Render the "Download" control: a toggle button plus a two-item format menu.
 *
 * @param props - The download handler.
 * @returns The menu element.
 */
export const DownloadMenu: React.FC<DownloadMenuProps> = ({ onDownload }) => {
  const [open, setOpen] = useState(false);

  const pick = (format: DocumentExportFormat): void => {
    setOpen(false);
    onDownload(format);
  };

  return (
    <div className="relative">
      <button
        type="button"
        data-testid="download-menu-toggle"
        aria-expanded={open}
        onClick={() => setOpen((prev) => !prev)}
        title="Download document"
        aria-label="Download document"
        className="h-8 px-2 rounded-md border border-slate-300 text-slate-700 hover:bg-slate-50 flex items-center gap-1"
      >
        <svg
          className="w-4 h-4 text-slate-600"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="2"
            d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"
          />
        </svg>
        <span aria-hidden="true" className="text-slate-400 text-[10px]">
          ▾
        </span>
      </button>
      {open && (
        <div
          data-testid="download-menu-panel"
          className="absolute right-0 z-30 mt-1 w-40 rounded-lg border border-slate-200 bg-white py-1 shadow-lg"
        >
          {FORMATS.map(({ format, label, testId }) => (
            <button
              key={format}
              type="button"
              data-testid={testId}
              onClick={() => pick(format)}
              className="block w-full px-3 py-1.5 text-left text-sm text-slate-700 hover:bg-slate-50"
            >
              {label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default DownloadMenu;
